import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { apiClient, ApiError } from '@/services/apiClient'
// F24 — the session-user wire type lives in packages/types (user.types.ts):
// POST /login and GET /users/me both emit the shared `SessionUser`
// (companies only for external users; `token` only on /login).
import type { SessionUser } from '@chemtracepro/types'

const TOKEN_KEY = 'chemtrace_auth_token'

// Kept as an alias so existing consumers (`type User` imports + the store
// surface) keep resolving to the SHARED session-user type.
export type User = SessionUser

interface LoginResponse {
  user?: SessionUser | null
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)
  const isExternalUser = computed(() => user.value?.userType === 'external')
  const primaryCompany = computed(() => user.value?.companies?.[0])

  async function login(email: string, password: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const data = await apiClient.post<LoginResponse>('/login', {
        user: { email, password },
      })

      user.value = data.user ?? null

      // Store JWT token for cross-site auth (cookies blocked by browsers)
      if (data.user?.token) {
        localStorage.setItem(TOKEN_KEY, data.user.token)
      }

      return true
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : String(err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      await apiClient.delete('/logout')
    } catch {
      // The API may reject or be unreachable — local state is cleared regardless.
    } finally {
      user.value = null
      localStorage.removeItem(TOKEN_KEY)
    }
  }

  async function fetchCurrentUser(): Promise<User | null> {
    try {
      const data = await apiClient.get<User>('/users/me')
      user.value = data
      return data
    } catch (err) {
      user.value = null
      if (err instanceof ApiError && err.statusCode === 401) {
        localStorage.removeItem(TOKEN_KEY)
      }
      return null
    }
  }

  async function checkAuth(): Promise<boolean> {
    if (user.value) return true
    const currentUser = await fetchCurrentUser()
    return currentUser !== null
  }

  return {
    user,
    isLoading,
    error,
    isAuthenticated,
    isExternalUser,
    primaryCompany,
    login,
    logout,
    fetchCurrentUser,
    checkAuth,
  }
})
